import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {DateRangeInput} from 'src/components/input';
import {McpLimitType} from 'src/openapi/admin/models/McpLimitType';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {SortHeaderFilter} from '@pb/kit-component-filters';
import {Group} from '@pb/kit-component-group';
import {SortDirections as KitSortDirections} from '@pb/kit-component-sort-header';
import {Cell, Head, Row} from '@pb/kit-component-table';

import {MCP_LIMIT_TYPE_LABELS} from './utils';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';


type Props = {
    limitTypes?: McpLimitType[],
    startDate?: string,
    endDate?: string,

    onLimitTypesChange: (values?: McpLimitType[]) => void,
    onDateFilterChange: (
        startDate: string | undefined,
        endDate: string | undefined,
    ) => void,

    sortDirection: SortDirection,
    onSort: (direction: 'ASC' | 'DESC') => void,
};

export const McpLimitLogTableHeader = ({
    limitTypes, startDate, endDate,
    onLimitTypesChange, onDateFilterChange,
    sortDirection, onSort,
}: Props) => {
    const [limitTypesDraft, setLimitTypesDraft] =
        useState<McpLimitType | undefined>(limitTypes?.[0]);
    const [startDateDraft, setStartDateDraft] =
        useState<string | undefined>(startDate);
    const [endDateDraft, setEndDateDraft] =
        useState<string | undefined>(endDate);
    const [dateClearKey, setDateClearKey] = useState(0);

    useEffect(() => {
        setLimitTypesDraft(limitTypes?.[0]);
    }, [limitTypes]);

    useEffect(() => {
        setStartDateDraft(startDate);
        setEndDateDraft(endDate);
    }, [startDate, endDate]);

    const hasLimitTypes = (limitTypes?.length || 0) > 0;

    const isFilterActive = useCallback(
        (field: 'limitTypes' | 'timestamp'): boolean => {
            if (field === 'limitTypes') {
                return hasLimitTypes;
            }
            return !!startDate || !!endDate;
        },
        [hasLimitTypes, startDate, endDate],
    );

    const limitTypeOptions = useMemo(
        () =>
            Object.values(McpLimitType).map(value => (
                <ComboboxItem
                    key={value}
                    caption={MCP_LIMIT_TYPE_LABELS[value]}
                    value={value}
                />
            )),
        [],
    );

    const applyLimitTypesFilter = useCallback(() => {
        if (limitTypesDraft) {
            onLimitTypesChange([limitTypesDraft]);
        } else {
            onLimitTypesChange();
        }
    }, [limitTypesDraft, onLimitTypesChange]);

    const clearLimitTypesFilter = useCallback(() => {
        setLimitTypesDraft(undefined);
        onLimitTypesChange();
    }, [onLimitTypesChange]);

    const applyDateFilter = useCallback(() => {
        onDateFilterChange(startDateDraft, endDateDraft);
    }, [startDateDraft, endDateDraft, onDateFilterChange]);

    const clearDateFilter = useCallback(() => {
        setStartDateDraft(undefined);
        setEndDateDraft(undefined);
        setDateClearKey(k => k + 1);
        onDateFilterChange(undefined, undefined);
    }, [onDateFilterChange]);

    return (
        <Head>
            <Row>
                <Cell rowSpan={2} width={280}>Account/Email</Cell>
                <Cell width={120} rowSpan={2}>
                    <SortHeaderFilter
                        caption="Limit Type"
                        sortable={false}
                        defaultDirection={KitSortDirections.ASC}
                        onApply={applyLimitTypesFilter}
                        onReset={clearLimitTypesFilter}
                        onCancel={() => setLimitTypesDraft(limitTypes?.[0])}
                        filterActive={isFilterActive('limitTypes')}>
                        <ComboBox
                            value={limitTypesDraft}
                            onChange={setLimitTypesDraft}
                            placeholder="Select Limit Type">
                            {limitTypeOptions}
                        </ComboBox>
                    </SortHeaderFilter>
                </Cell>
                <Cell colSpan={2} width={200}>Limit</Cell>
                <Cell colSpan={2} width={240}>Expiration Date</Cell>
                <Cell rowSpan={2} width={200}>Initiator</Cell>
                <Cell rowSpan={2} width={150}>
                    <SortHeaderFilter
                        caption="Timestamp"
                        sortable={true}
                        direction={sortDirection}
                        defaultDirection={KitSortDirections.DESC}
                        onSort={onSort}
                        onApply={applyDateFilter}
                        onReset={clearDateFilter}
                        onCancel={() => {
                            setStartDateDraft(startDate);
                            setEndDateDraft(endDate);
                        }}
                        filterActive={isFilterActive('timestamp')}>
                        <Group
                            key={dateClearKey}
                            direction="v"
                            style={{width: 236}}>
                            <DateRangeInput
                                id="timestamp-filter-start"
                                label="From"
                                clearable={true}
                                selectRangeType="start"
                                startDate={startDateDraft}
                                endDate={endDateDraft}
                                onChange={setStartDateDraft}
                                width={235}
                            />
                            <DateRangeInput
                                id="timestamp-filter-end"
                                label="To&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
                                clearable={true}
                                selectRangeType="end"
                                startDate={startDateDraft}
                                endDate={endDateDraft}
                                onChange={setEndDateDraft}
                                width={236}
                            />
                        </Group>
                    </SortHeaderFilter>
                </Cell>
            </Row>
            <Row>
                <Cell as="th">Old Value</Cell>
                <Cell as="th">New Value</Cell>
                <Cell as="th">Old Value</Cell>
                <Cell as="th">New Value</Cell>
            </Row>
        </Head>
    );
};
